import { useMemo } from "react";
import { useQuery } from "urql";
import type { ResultOf } from "@/data-access/graphql";
import { GetShareOfVoiceMetricsTableData } from "@/data-access/share-of-voice/queries/shareOfVoice-metrics-table-queries";
import { useShareOfVoiceParamsContextRequired } from "@/store/share-of-voice/params/shareOfVoice-params.context";

/**
 * Type for the Share of Voice metrics table data
 */
export type ShareVoiceMetricsTableData = NonNullable<
  ResultOf<typeof GetShareOfVoiceMetricsTableData>["shareVoiceTable"]
>[number];

/**
 * Hook to fetch Share of Voice metrics table data
 *
 * Fetches metrics data for the Share of Voice table including segments,
 * impact scores, sentiment data, and engagement metrics. Uses the current
 * date range from Share of Voice route context (`ShareOfVoiceParamsProvider`).
 *
 * @returns Object containing:
 * - data: Array of ShareVoiceTable data or undefined
 * - fetching: Boolean indicating if the query is in progress
 * - error: Any error that occurred during the query
 * - refetch: Function to manually refetch the data
 */
export const useGetShareOfVoiceMetricsTableData = () => {
  const { startDate, endDate, filtersAnd } =
    useShareOfVoiceParamsContextRequired();

  const [{ data, fetching, error, stale }, refetch] = useQuery({
    query: GetShareOfVoiceMetricsTableData,
    variables: {
      projectionFilters: {
        dateRange: {
          startDate,
          endDate,
        },
        AND: filtersAnd.AND,
        state: "ADDED",
      },
    },
    requestPolicy: "cache-and-network",
  });

  return {
    data: data?.shareVoiceTable,
    fetching,
    isRefetching: stale,
    error,
    refetch,
  };
};

/**
 * Hook that returns a function to calculate the total sum of a specific field across all rows
 *
 * @returns A function that calculates column totals
 */
export const useCalculateColumnTotal = () => {
  return useMemo(() => {
    return (
      data: ShareVoiceMetricsTableData[] | undefined,
      field: keyof ShareVoiceMetricsTableData,
    ) => {
      if (!data) return 0;
      return data.reduce((sum, row) => sum + ((row[field] as number) || 0), 0);
    };
  }, []);
};

/**
 * Categorizes sentiment values into POSITIVE, NEGATIVE, or NEUTRAL
 *
 * @param sentimentValue - The numeric sentiment value
 * @returns "POSITIVE" if value > 20, "NEGATIVE" if value < -20, "NEUTRAL" otherwise
 */
export const categorizeSentiment = (sentimentValue: number): string => {
  if (sentimentValue > 20) {
    return "Positive";
  }
  if (sentimentValue < -20) {
    return "Negative";
  }
  return "Neutral";
};
